import { useEffect, useState, useRef } from 'react';
import { Box, useTheme, Button } from "@mui/material";
import { DataGrid, GridToolbar } from "@mui/x-data-grid";
import { tokens, useHideMode } from "../theme";
import SaveIcon from '@mui/icons-material/Save';
import Header from "../components/Header";
import useAxiosPrivate from "../hooks/useAxiosPrivate"
import saveMoneyAccounts from './managers/saveMoneyAccounts';
import { useNavigate, useLocation } from 'react-router-dom';
import handleError from "../hooks/handleError";
import { ToastContainer, toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import SyncAltIcon from '@mui/icons-material/SyncAlt';
import RotateRightIcon from '@mui/icons-material/RotateRight';
import TwoFactorAuthPopup from '../components/TwoFactorAuthPopup';


const MoneyAccounts = () => {
    useEffect(() => {
        document.title = 'Fretless Finance | Money Accounts';
    }, []);

    const theme = useTheme();
    const colors = tokens(theme.palette.mode);
    const hideTheme = useHideMode();
    const axiosPrivate = useAxiosPrivate();
    const navigate = useNavigate();
    const location = useLocation();
    var [money_accounts, setMoneyAccounts] = useState([]);
    var [DisplayedMoneyAccounts, setDisplayedMoneyAccounts] = useState([]);
    var [saveMoneyAcctsError, setSaveMoneyAcctsErr] = useState('');
    var err = false;
    const errRef = useRef();
    const effectRan = useRef(false);
    var isMounted = false;
    const controller = new AbortController();
    const [loading, setLoading] = useState(false);
    const [openTwoFactorAuth, setOpenTwoAuth] = useState(false);
    const [twoFATitle, set2FATitle] = useState();
    const [twoFA_answer_url, set2FAAnswerURL] = useState();

    // Get Money Accounts
    useEffect(() => {
        if (effectRan.current === false) {
            let isMounted = true;
            const controller = new AbortController();

            const getMoneyAccounts = async () => {
                try {
                    var getMoneyAccountsResponse = await axiosPrivate.get('/money-accounts', {
                        signal: controller.signal
                    });
                    isMounted && setMoneyAccounts(getMoneyAccountsResponse.data)
                    setDisplayedMoneyAccounts(getMoneyAccountsResponse.data);
                } catch (err) {
                    if ((err.response?.status === 401) || (err.response?.status === 403)) {
                        navigate('/sign-in', { state: { from: location }, replace: true });
                    }
                    else if ((err.response?.status === 400) & (err.response?.data?.detail === "Personal Capital login requires Two Factor Authentication")) {
                        // handle pop up for 2FA
                        set2FAAnswerURL("/connected-accounts/personal-capital/2fa-answer");
                        set2FATitle("Empower");
                        setOpenTwoAuth(true);
                    }

                    else {
                        handleError(err);
                    }
                }
            }

            getMoneyAccounts();
            effectRan.current = true;
        }

        return () => {
            isMounted = false;
            controller.abort();

        }

    }, [])

    const fetchMoneyAccounts = async () => {
        setLoading(true);
        try {
            var Response = await axiosPrivate.get('/money-accounts/fetch', {
                signal: controller.signal
            });

            setMoneyAccounts(Response.data)
            setDisplayedMoneyAccounts(Response.data);
        } catch (err) {
            if ((err.response?.status === 401) || (err.response?.status === 403)) {
                navigate('/sign-in', { state: { from: location }, replace: true });
            }
            else if ((err.response?.status === 400) & (err.response?.data?.detail === "Personal Capital login requires Two Factor Authentication")) {
                // handle pop up for 2FA
                set2FAAnswerURL("/connected-accounts/personal-capital/2fa-answer");
                set2FATitle("Empower");
                setOpenTwoAuth(true);
            }

            else {
                handleError(err);
            }
        }
        setLoading(false);
    }


    const handleUpdateClick = event => {
        fetchMoneyAccounts();
    }

    const processRowUpdate = (newRow) => {
        const updatedRow = { ...newRow, actions: ((newRow.actions !== "INSERT" & newRow.actions !== "DELETE") ? "UPDATE" : newRow.actions) };
        setMoneyAccounts(money_accounts.map((row) => (row.id === updatedRow.id ? updatedRow : row)));
        setDisplayedMoneyAccounts(DisplayedMoneyAccounts.map((row) => ((row.id === updatedRow.id & updatedRow.actions !== "DELETE") ? updatedRow : row)));
        return updatedRow;
    };

    const saveChangesHandler = event => {
        try {
            err = saveMoneyAccounts(money_accounts);
        } catch (err) {
            err = true;
            if (!err?.response) {
                setSaveMoneyAcctsErr('No Server Response');
            } else if (err.response?.status === 500) {
                setSaveMoneyAcctsErr('Backend Error, check your fields');
            } else if (err.response?.status === 401) {
                setSaveMoneyAcctsErr('Unauthorized')
            } else {
                setSaveMoneyAcctsErr('Error')
            }
        }

        if (!err) {
            var updatedArray = money_accounts.filter((row) => row.actions !== "DELETE")
            updatedArray = updatedArray.map(row => ({ ...row, actions: null }));
            setMoneyAccounts(updatedArray)
        }
        err = false;
    }

    const MoneyAccountsColumns = [
        {
            field: "name",
            headerName: "Name",
            headerAlign: 'center',
            width: 200,
            align: 'left',
            editable: true,
        },
        {
            field: "account_type",
            headerName: "Type",
            headerAlign: 'center',
            width: 150,
            align: 'left',
            editable: false,
        },
        {
            field: "pc_current_balance",
            headerName: "Balance",
            headerAlign: 'center',
            width: 100,
            align: 'right',
            editable: false,
            valueFormatter: (params) => hideTheme.mode === "show" ? params.value : '*'.repeat(9),
        },
        {
            field: "pc_running_balance",
            headerName: "Running Balance",
            headerAlign: 'center',
            width: 150,
            align: 'right',
            editable: false,
            valueFormatter: (params) => hideTheme.mode === "show" ? params.value : '*'.repeat(9),
        },
        {
            field: "currency",
            headerName: "Currency",
            headerAlign: 'center',
            width: 100,
            align: 'center',
            editable: false,
        },
        {
            field: "amount",
            headerName: "Amount",
            headerAlign: 'center',
            width: 100,
            align: 'right',
            editable: false,
            valueFormatter: (params) => hideTheme.mode === "show" ? params.value : '*'.repeat(9),
        },
        {
            field: "status",
            headerName: "Status",
            headerAlign: 'center',
            width: 100,
            cellClassName: "name-column--cell",
            editable: false,
        },
        {
            field: "pc_firm_name",
            headerName: "Firm Name",
            headerAlign: 'center',
            width: 350,
            editable: false,
        },
        {
            field: "app_account",
            headerName: "Account",
            headerAlign: 'center',
            width: 200,
            align: 'center',
            editable: true,
        },


    ];

    return (
        <Box m="20px">
            <TwoFactorAuthPopup
                answerURL={twoFA_answer_url?.toString()}
                connected_account={twoFATitle}
                openTwoAuth={openTwoFactorAuth}
                setOpenTwoAuth={setOpenTwoAuth}>
            </TwoFactorAuthPopup>
            <ToastContainer />

            <Box display="flex" justifyContent="space-between" alignItems="center">
                <Header
                    title="Money Accounts"
                    titleSize="h2"
                    subtitle="Places Your Money Resides"
                    subtitleSize="h5"
                />

                <Box>

                    {(loading) ?
                        <RotateRightIcon
                            className="rotate-icon"

                            style={{ marginRight: "0%" }}
                        />
                        :
                        ""
                    }

                    <Button
                        onClick={() => { handleUpdateClick() }}
                        sx={{
                            marginRight: "0%",
                            backgroundColor: colors.blueAccent[400],
                            color: "#ffffff",
                            '&:hover': {
                                backgroundColor: colors.blueAccent[100],
                                color: "#ffffff",
                            },
                            fontSize: "14px",
                            fontWeight: "bold",
                            padding: "10px 20px",
                        }}
                    >
                        <SyncAltIcon sx={{ mr: "10px" }} />
                        Update Accounts
                    </Button>
                </Box>
            </Box>


            <Box
                height="67vh"
                sx={{
                    "& .MuiDataGrid-root": {
                        border: "none",
                    },
                    "& .MuiDataGrid-cell": {
                        borderBottom: "none",
                    },
                    "& .name-column--cell": {
                        color: colors.greenAccent[300],
                    },
                    "& .MuiDataGrid-columnHeaders": {
                        backgroundColor: colors.blueAccent[400],
                        borderBottom: "none",
                    },
                    "& .MuiDataGrid-virtualScroller": {
                        backgroundColor: colors.primary[400],
                    },
                    "& .MuiDataGrid-footerContainer": {
                        borderTop: "none",
                        backgroundColor: colors.blueAccent[400],
                    },
                    "& .MuiCheckbox-root": {
                        color: `${colors.greenAccent[200]} !important`,
                    },
                    "& .MuiDataGrid-toolbarContainer .MuiButton-text": {
                        color: `${colors.grey[100]} !important`,
                    },
                }}
            >

                < DataGrid

                    rows={DisplayedMoneyAccounts}
                    columns={MoneyAccountsColumns}
                    processRowUpdate={processRowUpdate}
                    experimentalFeatures={{ newEditingApi: true }}
                    components={{ Toolbar: GridToolbar }
                    }
                    sx={
                        {
                            fontSize: 15,
                            '& .MuiDataGrid-cell:edit': {
                                fontSize: 10,
                            },
                            CanUserResizeColumns: true,
                        }

                    }
                />
            </Box>
            <Box padding="1rem" align="right">
                <p ref={errRef} className={saveMoneyAcctsError ? "errmsg" :
                    "offscreen"} aria-live="assertive">{saveMoneyAcctsError}</p>
                <Button onClick={saveChangesHandler}
                    sx={{
                        backgroundColor: colors.greenAccent[500],
                        color: "#ffffff",
                        '&:hover': {
                            backgroundColor: colors.greenAccent[300],
                            color: "#ffffff",
                        },
                        fontSize: "14px",
                        fontWeight: "bold",
                        padding: "10px 20px",
                    }}
                >
                    <SaveIcon sx={{ mr: "10px" }} />
                    Save Changes
                </Button>
            </Box>
        </Box>

    )
};


export default MoneyAccounts;


